/**
 * @fileoverview Combo bar + share-of-total line chart with real-geometry label-collision
 * avoidance.
 * @description Ported from the InsightBox Banking design system's own `charts/ComboBarDot.tsx` —
 * bars on one y-axis (a raw count/amount), a line + %-share label on a second, independently-scaled
 * y-axis. The label-overlap measurement (checking a % label against the zigzag line's own segments,
 * every bar's rendered box, and its left neighbor's label box, then pushing it up until clear) is
 * real, carefully-tuned geometry solving a genuinely hard problem (two independently-scaled axes
 * mean a tall bar can intrude on an unrelated point's label), so it's kept intact including the
 * original explanatory comments. Only the color-constant mapping (`CHART_NAVY`/`CHART_BRAND` here
 * map to this app's `CHART_ACCENT`/`CHART_PRIMARY` — see `TrendArea.tsx`'s file comment) and the
 * render-prop/tooltip/legend typing (recharts' own exported types instead of the reference's
 * untyped `any`) were adapted. Feature-scoped rather than promoted to `shared/` since
 * `ComboBarDotPoint` is a banking-specific data shape.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useLayoutEffect, useRef, useState } from 'react'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import {
  CHART_ACCENT,
  CHART_ERROR,
  CHART_GRID,
  CHART_PRIMARY,
  CHART_SUCCESS,
} from '@/shared/constants/chart-theme.constants'

import type { LabelProps, TooltipContentProps } from 'recharts'

export interface ComboBarDotPoint {
  label: string
  count: number
  sharePct: number
  /** Same share metric for the prior year, if available — shown only in the hover tooltip (as a
   * +/- pp change) so the always-visible chart doesn't get a second label under every point. */
  prevSharePct?: number
}

const LABEL_BASE_OFFSET = 12
const LABEL_CLEARANCE = 6

function makeSharePctLabel(data: ComboBarDotPoint[], extraOffsets: Record<number, number>) {
  return function SharePctLabel(props: LabelProps) {
    const { x, y, index } = props
    const point = index === undefined ? undefined : data[index]
    if (!point || typeof x !== 'number' || typeof y !== 'number') return null
    const extra = extraOffsets[index!] || 0
    return (
      <text
        data-share-label={index}
        x={x}
        y={y - LABEL_BASE_OFFSET - extra}
        textAnchor="middle"
        fill={CHART_PRIMARY}
        className="text-[12px] font-bold"
      >
        {point.sharePct.toFixed(2)}%
      </text>
    )
  }
}

// The % labels sit above their dot by a fixed offset, but three things can still intrude on them:
// (1) the trend line is a zigzag (troughs flanked by taller neighbors), so a segment connecting two
// much-taller points can cut straight through a lower point's label; (2) a bar's own height is on a
// completely separate axis from the dot/label, so for data where the two aren't correlated (e.g.
// growth% reused as the line series) a tall bar can sit right under, or into, a label that "should"
// be well clear of it; (3) tightly-packed points (many branches/months) can put two neighboring
// labels close enough horizontally to overlap each other even though each individually clears its
// own line/bar. This measures the actual rendered geometry for all three and pushes an intruded
// label further up until it's clear, instead of guessing a single offset that only works for one
// dataset/viewport size.
function clearLabelOverlaps(
  container: HTMLDivElement,
  setExtraOffsets: (updater: (prev: Record<number, number>) => Record<number, number>) => void,
) {
  const svg = container.querySelector('svg')
  if (!svg) return
  // The compound selectors below (a class combined with a descendant tag, or an attribute
  // selector) don't match `querySelectorAll`'s literal-tag-name overload, so its generic type
  // parameter defaults to plain `Element` — these casts restore the real element type each list is
  // known to hold, needed below for `getBBox()` (declared on `SVGGraphicsElement`, not `Element`).
  // `no-unnecessary-type-assertion` misreads this specific `Array.from(NodeListOf<Element>)`
  // pattern as already narrowed (confirmed false positive: removing the cast reintroduces a real
  // `getBBox` type error, caught by `no-unsafe-call` instead).
  /* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
  const dots = Array.from(svg.querySelectorAll('.recharts-line-dots circle')) as SVGCircleElement[]
  if (dots.length < 2) return
  const dotPositions = dots.map((c) => ({
    cx: Number.parseFloat(c.getAttribute('cx') || '0'),
    cy: Number.parseFloat(c.getAttribute('cy') || '0'),
  }))
  const barEls = Array.from(svg.querySelectorAll('.recharts-bar-rectangle')) as SVGGraphicsElement[]
  const labelEls = Array.from(svg.querySelectorAll('text[data-share-label]')) as SVGTextElement[]
  /* eslint-enable @typescript-eslint/no-unnecessary-type-assertion */

  setExtraOffsets((prev) => {
    let changed = false
    const next = { ...prev }
    // How far each label has already been pushed up WITHIN this pass, so a chain of several
    // adjacent tall labels resolves in one measurement pass instead of needing a resize event to
    // trigger each follow-up step (this effect only re-measures on resize, not on every offset
    // change).
    const pendingShift: Record<number, number> = {}

    const bboxByIndex = new Map<number, DOMRect>()
    labelEls.forEach((el) => {
      const idx = Number(el.getAttribute('data-share-label'))
      try {
        bboxByIndex.set(idx, el.getBBox())
      } catch {
        // not measurable yet (e.g. not yet laid out) — skip this pass
      }
    })

    Array.from(bboxByIndex.keys())
      .sort((a, b) => a - b)
      .forEach((idx) => {
        const bbox = bboxByIndex.get(idx)!
        const x0 = bbox.x
        const x1 = bbox.x + bbox.width

        let minY = Number.POSITIVE_INFINITY

        // Obstacle 1: the connecting line's zigzag segments.
        for (let i = 0; i < dotPositions.length - 1; i++) {
          const a = dotPositions[i]!
          const b = dotPositions[i + 1]!
          const segX0 = Math.min(a.cx, b.cx)
          const segX1 = Math.max(a.cx, b.cx)
          if (segX1 < x0 || segX0 > x1) continue
          const steps = 24
          for (let s = 0; s <= steps; s++) {
            const t = s / steps
            const x = a.cx + t * (b.cx - a.cx)
            if (x < x0 || x > x1) continue
            const y = a.cy + t * (b.cy - a.cy)
            if (y < minY) minY = y
          }
        }

        // Obstacle 2: any bar (not just this point's own) whose rect overlaps this label's
        // x-range — the bar and dot/label axes are independently scaled, so any bar can intrude.
        barEls.forEach((barEl) => {
          let bb: DOMRect
          try {
            bb = barEl.getBBox()
          } catch {
            return
          }
          const bx0 = bb.x
          const bx1 = bb.x + bb.width
          if (bx1 < x0 || bx0 > x1) return
          if (bb.y < minY) minY = bb.y
        })

        // Obstacle 3: the label immediately to the left, if it's close enough to overlap
        // horizontally — only the left neighbor is treated as an obstacle (never the right one) so
        // the adjustment is strictly one-directional and can't oscillate between the two. Its
        // position accounts for any shift already decided earlier in this same pass, not just its
        // pre-pass rendered position.
        const leftBbox = bboxByIndex.get(idx - 1)
        if (leftBbox) {
          const lx0 = leftBbox.x
          const lx1 = leftBbox.x + leftBbox.width
          const leftTop = leftBbox.y - (pendingShift[idx - 1] || 0)
          if (!(lx1 < x0 || lx0 > x1) && leftTop < minY) minY = leftTop
        }

        if (minY === Number.POSITIVE_INFINITY) return

        const requiredBottom = minY - LABEL_CLEARANCE
        const currentBottom = bbox.y + bbox.height
        const extraNeeded = currentBottom - requiredBottom
        if (extraNeeded > 0.5) {
          pendingShift[idx] = extraNeeded
          const updated = (prev[idx] || 0) + extraNeeded
          if (Math.abs(updated - (prev[idx] || 0)) > 0.5) {
            next[idx] = updated
            changed = true
          }
        }
      })

    return changed ? next : prev
  })
}

function makeComboTooltip(
  barName: string,
  shareLabel: string,
  countFormatter: (v: number) => string,
) {
  return function ComboTooltip({ active, payload }: TooltipContentProps) {
    if (!active || !payload?.length) return null
    // recharts types a tooltip payload entry's own `payload` (the underlying data point) as `any`
    // in its own public API — there's no narrower type to reach for here, so this one assertion is
    // the actual "the payload for this chart's tooltip entry is a ComboBarDotPoint" contract, not a
    // shortcut around a real type.
    const point = payload[0]!.payload as ComboBarDotPoint
    const delta = point.prevSharePct !== undefined ? point.sharePct - point.prevSharePct : null
    return (
      <div className="rounded-xl border border-border bg-white px-3.5 py-2.5 shadow-card">
        <p className="text-xs font-semibold text-label">{point.label}</p>
        <p className="mt-1 text-sm font-bold text-accent">{countFormatter(point.count)}</p>
        <p className="text-[11px] text-label">{barName}</p>
        <div className="mt-1.5 space-y-0.5 border-t border-border pt-1.5 text-xs">
          <p className="flex items-center justify-between gap-4">
            <span className="text-label">{shareLabel}</span>
            <span className="font-semibold text-primary">{point.sharePct.toFixed(2)}%</span>
          </p>
          {delta !== null && (
            <>
              <p className="flex items-center justify-between gap-4">
                <span className="text-label">Prior Year Share</span>
                <span className="font-semibold text-text">{point.prevSharePct!.toFixed(2)}%</span>
              </p>
              <p className="flex items-center justify-between gap-4 font-semibold text-text">
                <span>YoY Change</span>
                <span style={{ color: delta >= 0 ? CHART_SUCCESS : CHART_ERROR }}>
                  {delta >= 0 ? '+' : ''}
                  {delta.toFixed(2)} pp
                </span>
              </p>
            </>
          )}
        </div>
      </div>
    )
  }
}

function ComboLegend({ barName, shareLabel }: { barName: string; shareLabel: string }) {
  return (
    <ul className="flex flex-wrap items-center justify-end gap-5 pr-4 pb-3 text-sm text-label">
      <li className="flex items-center gap-2">
        <span
          className="inline-block h-3 w-3 rounded-[3px]"
          style={{ backgroundColor: CHART_ACCENT }}
        />
        {barName}
      </li>
      <li className="flex items-center gap-2">
        <span
          className="inline-block h-3 w-3 rounded-full"
          style={{ backgroundColor: CHART_PRIMARY }}
        />
        {shareLabel}
      </li>
    </ul>
  )
}

interface ComboBarDotProps {
  data: ComboBarDotPoint[]
  barName: string
  /** Label for the dot/line series (defaults to "% of Total") — override when `sharePct` is
   * repurposed to carry a different metric, e.g. growth% instead of a share-of-total. */
  shareLabel?: string
  countFormatter?: (v: number) => string
  height?: number | '100%'
}

/**
 * @component
 */
function ComboBarDot({
  data,
  barName,
  shareLabel = '% of Total',
  countFormatter = (v: number) => v.toLocaleString('en-IN'),
  height = 380,
}: ComboBarDotProps) {
  const countMax = Math.max(...data.map((d) => d.count))
  const shareMax = Math.max(...data.map((d) => d.sharePct))

  const dataKey = data.map((d) => `${d.label}:${d.sharePct}`).join('|')
  const [prevKey, setPrevKey] = useState(dataKey)
  const [labelExtraOffsets, setLabelExtraOffsets] = useState<Record<number, number>>({})
  if (dataKey !== prevKey) {
    setPrevKey(dataKey)
    setLabelExtraOffsets({})
  }

  const wrapRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const container = wrapRef.current
    if (!container) return
    clearLabelOverlaps(container, setLabelExtraOffsets)
    // A live window drag fires ResizeObserver dozens of times per second; debouncing so the
    // recompute only runs once resizing has paused avoids re-measuring (and potentially
    // re-adjusting label positions) on every single one of those, which was visible as flicker
    // mid-drag.
    let debounce: ReturnType<typeof setTimeout> | null = null
    const ro = new ResizeObserver(() => {
      if (debounce) clearTimeout(debounce)
      debounce = setTimeout(() => {
        requestAnimationFrame(() => {
          if (wrapRef.current) clearLabelOverlaps(wrapRef.current, setLabelExtraOffsets)
        })
      }, 150)
    })
    ro.observe(container)
    return () => {
      if (debounce) clearTimeout(debounce)
      ro.disconnect()
    }
  }, [dataKey])

  const SharePctLabel = makeSharePctLabel(data, labelExtraOffsets)
  const ComboTooltip = makeComboTooltip(barName, shareLabel, countFormatter)

  // The bars get generous headroom (count axis tops out well above the tallest bar) while the %
  // line gets very little (its axis tops out just above the highest point) — since sharePct is a
  // direct function of count, this is what actually keeps the dots/labels riding clear above the
  // bar tops instead of sinking into them (a symmetric ~1.2x on both axes puts them at nearly the
  // same height, which is what was clipping).
  const countAxisMax = Math.ceil((countMax * 1.45) / 100) * 100
  const countMin = Math.min(0, ...data.map((d) => d.count))
  const countAxisMin = countMin < 0 ? Math.floor((countMin * 1.45) / 100) * 100 : 0
  const shareMin = Math.min(0, ...data.map((d) => d.sharePct))
  const shareAxisMax = Math.ceil(shareMax * 1.1 * 10) / 10
  const shareAxisMin = shareMin < 0 ? Math.floor(shareMin * 1.1 * 10) / 10 : 0

  return (
    // Absolutely fill the nearest positioned ancestor (the page's own wrapper div, given
    // `relative`) instead of `height: 100%` — a plain block child's percentage height only reliably
    // resolves against a parent with an explicit height, which a flex-grow-sized ancestor doesn't
    // consistently provide (confirmed: it silently collapsed to 0 here). An absolutely positioned
    // box resolves against the used value of its containing block instead, which does work in that
    // case.
    <div ref={wrapRef} className="absolute inset-0">
      <ResponsiveContainer width="100%" height={height} debounce={200}>
        <ComposedChart data={data} margin={{ top: 40, right: 16, left: 8, bottom: 8 }}>
          <CartesianGrid stroke={CHART_GRID} vertical={false} />
          <XAxis
            dataKey="label"
            axisLine={false}
            tickLine={false}
            interval={0}
            angle={-20}
            textAnchor="end"
            height={64}
            tick={{ fill: '#111827', fontSize: 11, fontWeight: 500 }}
          />
          <YAxis
            yAxisId="count"
            domain={[countAxisMin, countAxisMax]}
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#6B7280', fontSize: 11 }}
            width={44}
          />
          <YAxis
            yAxisId="share"
            orientation="right"
            domain={[shareAxisMin, shareAxisMax]}
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#6B7280', fontSize: 11 }}
            width={40}
            unit="%"
          />
          <Tooltip content={ComboTooltip} />
          <Legend
            verticalAlign="top"
            align="right"
            height={36}
            content={<ComboLegend barName={barName} shareLabel={shareLabel} />}
          />
          {/* isAnimationActive=false, matching the Line below — the label overlap measurement runs
              in a useLayoutEffect right at mount, before an animated bar's grow-in transition
              finishes, so it would otherwise measure each bar's transient near-zero height instead
              of its final one and never detect a real collision. */}
          <Bar
            yAxisId="count"
            dataKey="count"
            name={barName}
            fill={CHART_ACCENT}
            radius={[6, 6, 0, 0]}
            barSize={30}
            isAnimationActive={false}
          />
          <Line
            yAxisId="share"
            dataKey="sharePct"
            name={shareLabel}
            stroke={CHART_PRIMARY}
            strokeWidth={2}
            isAnimationActive={false}
            dot={{ r: 4, fill: CHART_PRIMARY, stroke: '#fff', strokeWidth: 1.5 }}
            label={SharePctLabel}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}

export default ComboBarDot
